import { WineHistory, GroupedHistoryEntry, HistoryOccasion } from '../types';
import { getWineDisplayName } from '../utils';

interface HistoryModalProps {
  wineHistory: WineHistory[];
  onAddFromHistory: (entry: GroupedHistoryEntry) => void;
  onDeleteOccasion: (occasion: HistoryOccasion) => void;
  onViewWine: (canonicalWineId: string) => void;
  onClose: () => void;
}

export function HistoryModal({ wineHistory, onAddFromHistory, onDeleteOccasion, onViewWine, onClose }: HistoryModalProps) {
  const groupedHistory = wineHistory.reduce<Record<string, GroupedHistoryEntry>>((groups, entry) => {
    const key = `${(entry.wine_name || '').toLowerCase()}-${(entry.producer || '').toLowerCase()}-${entry.vintage || ''}`;
    if (!groups[key]) {
      groups[key] = {
        wine_name: entry.wine_name,
        producer: entry.producer,
        vintage: entry.vintage,
        country: entry.country,
        region: entry.region,
        appellation: entry.appellation,
        varietal: entry.varietal,
        color: entry.color,
        wine_type: entry.wine_type,
        bottle_size: entry.bottle_size,
        purchase_price: entry.purchase_price,
        canonical_wine_id: entry.canonical_wine_id,
        occasions: [],
        total_consumed: 0
      };
    }
    if (entry.canonical_wine_id && !groups[key].canonical_wine_id) {
      groups[key].canonical_wine_id = entry.canonical_wine_id;
    }
    groups[key].occasions.push({
      id: entry.id,
      date_drank: entry.date_drank,
      occasion_notes: entry.occasion_notes,
      rating: entry.rating,
      quantity_consumed: 1
    });
    groups[key].total_consumed = (groups[key].total_consumed || 0) + 1;
    return groups;
  }, {});

  const sortedGroups = Object.values(groupedHistory).sort((a, b) => {
    const aDate = Math.max(...a.occasions.map(o => new Date(o.date_drank).getTime()));
    const bDate = Math.max(...b.occasions.map(o => new Date(o.date_drank).getTime()));
    return bDate - aDate;
  });

  const handleDelete = async (occasion: HistoryOccasion) => {
    onDeleteOccasion(occasion);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        style={{ height: '85vh', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ width: '50px' }} />
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            📜 Drinking History
          </h2>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '16px',
              background: 'var(--color-parchment-dark)',
              border: 'none',
              color: 'var(--color-text)',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '0 16px 24px'
        }}>
          {wineHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>🍷</p>
              <p style={{ fontSize: '18px', color: 'var(--color-text)', marginBottom: '8px' }}>
                No bottles enjoyed yet
              </p>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                Wines you drink will appear here
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sortedGroups.map((group, groupIndex) => {
                const displayName = getWineDisplayName(group);
                const hasCanonical = !!group.canonical_wine_id;

                return (
                  <div 
                    key={groupIndex} 
                    className="ios-card"
                    style={{ padding: '16px' }}
                  >
                    {hasCanonical ? (
                      <button 
                        onClick={() => onViewWine(group.canonical_wine_id!)} 
                        style={{ 
                          width: '100%', 
                          textAlign: 'left', 
                          background: 'none', 
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{ fontWeight: '600', color: 'var(--color-wine)', marginBottom: '2px' }}>
                              {displayName} {group.vintage && <span style={{ fontWeight: 'normal' }}>{group.vintage}</span>}
                            </p>
                            {group.wine_name && group.producer && (
                              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>{group.producer}</p>
                            )}
                            {group.region && (
                              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>{group.region}</p>
                            )}
                          </div>
                          <span className="badge" style={{ background: 'var(--color-parchment-dark)', color: 'var(--color-text)' }}>
                            {group.occasions.length}×
                          </span>
                        </div>
                      </button>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <p style={{ fontWeight: '600', color: 'var(--color-text)', marginBottom: '2px' }}>
                            {displayName} {group.vintage && <span style={{ fontWeight: 'normal' }}>{group.vintage}</span>}
                          </p>
                          {group.wine_name && group.producer && (
                            <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>{group.producer}</p>
                          )}
                          {group.region && (
                            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>{group.region}</p>
                          )}
                        </div>
                        <span className="badge" style={{ background: 'var(--color-parchment-dark)', color: 'var(--color-text)' }}>
                          {group.occasions.length}×
                        </span>
                      </div>
                    )}

                    {/* Occasions */}
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid var(--color-border)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      {group.occasions
                        .sort((a, b) => new Date(b.date_drank).getTime() - new Date(a.date_drank).getTime())
                        .map((occasion) => (
                          <div 
                            key={occasion.id} 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'flex-start', 
                              justifyContent: 'space-between',
                              fontSize: '14px'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <span style={{ color: 'var(--color-text)' }}>
                                🍷 {new Date(occasion.date_drank).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                              {occasion.rating && (
                                <span className="badge badge-gold" style={{ marginLeft: '8px' }}>
                                  {occasion.rating} pts
                                </span>
                              )}
                              {occasion.occasion_notes && (
                                <p style={{ 
                                  fontSize: '12px', 
                                  fontStyle: 'italic', 
                                  color: 'var(--color-text-muted)', 
                                  marginTop: '4px' 
                                }}>
                                  "{occasion.occasion_notes}"
                                </p>
                              )}
                            </div>
                            <button 
                              onClick={() => handleDelete(occasion)} 
                              style={{
                                marginLeft: '8px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                background: '#fee2e2',
                                color: '#991b1b',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                    </div>

                    {/* Add to Cellar button */}
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--color-border)' }}>
                      <button 
                        onClick={() => onAddFromHistory(group)} 
                        className="ios-btn ios-btn-secondary w-full"
                        style={{ fontSize: '14px' }}
                      >
                        ↩ Add to Cellar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
